import React from 'react';
import i18n from 'services/i18n';
import { Trans } from 'react-i18next';
import store from 'redux/store';
import { compact, filter, includes, isEmpty, orderBy } from 'lodash-es';
import {
  ACT_CODE,
  CAN_CODE,
  CLOSE_CODE,
  ERROR_CODE,
  EXPIRATION_TEXT,
  PAYMENT_NEXT_ACTIONS,
  THANK_YOU_TEXT_DESKTOP
} from 'consts';
import { isAnotherPaymentAllowed } from 'utilities';
import { getDateFormatted } from 'utilities/global';

export const getCategories = (paymentMethodTypeCategories, paymentMethodTypes) => {
  const categories = isEmpty(paymentMethodTypeCategories)
    ? paymentMethodTypes
    : filter(paymentMethodTypes, item =>
        includes(paymentMethodTypeCategories, item.category)
      );

  return orderBy(categories, ['order', category => category.category.toLowerCase()]);
};

export const findCategoryTypes = (paymentMethodTypeCategories, selectedCategory) =>
  orderBy(
    paymentMethodTypeCategories.find(
      category => category.category.split('_')[0] === selectedCategory
    )?.types ?? [],
    ['order', pmt => pmt.name.toLowerCase()]
  );

export const getCategoryPaymentMethodTypes = (paymentMethodTypes, category) => {
  const relevantPaymentMethodTypes =
    paymentMethodTypes.find(item => item.category === category)?.types || [];
  return orderBy(relevantPaymentMethodTypes, ['order', pmt => pmt.name.toLowerCase()]);
};

export const isFailedPayment = status => [CAN_CODE, ERROR_CODE].includes(status);

export const isPendingWithInstructions = ({ isThereInstructions, status, category }) => {
  const validCategories = ['ewallet', 'cash', 'bank_transfer'];
  const isValidCategory = validCategories.includes(category);

  return isValidCategory && status === ACT_CODE && isThereInstructions;
};
export const isDelayedPayment = ({ nextAction, status }) =>
  status === ACT_CODE && nextAction === PAYMENT_NEXT_ACTIONS.PendingCapture;

export const is3DSPending = ({ nextAction, status }) =>
  nextAction === PAYMENT_NEXT_ACTIONS.ThreeDSVerification && status === ACT_CODE;

export const isAwaitingNextAction = ({ nextAction, status }) =>
  !!nextAction &&
  ![PAYMENT_NEXT_ACTIONS.PendingCapture, PAYMENT_NEXT_ACTIONS.NotApplicable].includes(
    nextAction
  ) &&
  status === ACT_CODE;

export const isPaymentSuccess = ({ nextAction, status }) =>
  status === CLOSE_CODE ||
  (status === ACT_CODE &&
    (!nextAction ||
      [PAYMENT_NEXT_ACTIONS.NotApplicable, PAYMENT_NEXT_ACTIONS.PendingCapture].includes(
        nextAction
      )));

export const canMakeAnotherPayment = ({ paymentStatus, paymentsMetadata }) => {
  const { payments_created: paymentsCreated, user_agent: userAgent } =
    paymentsMetadata || {};
  const { max_create_payment_tries: maxPayments } =
    store.getState().global?.configData || {};

  return (
    isFailedPayment(paymentStatus) &&
    isAnotherPaymentAllowed({
      paymentsCreated,
      maxPayments,
      userAgent
    })
  );
};

export const getTextualCodeText = ({
  category,
  paymentStatus,
  expiration,
  isThereInstructions,
  pdfLink,
  blocked,
  nextAction
}) => {
  const expirationText = getExpirationText(expiration, i18n.language);

  const getPendingText = isPdf => {
    if (isPdf) return i18n.t('text_pending_payment_pdf');

    const pendingText = isThereInstructions
      ? 'thankYouTexts.subTitles.text_pending_payment_with_instructions'
      : 'thankYouTexts.subTitles.text_pending_payment_without_instructions';

    return (
      <>
        <Trans i18nKey={pendingText} components={{ bold: <strong /> }} />
        {expirationText}
      </>
    );
  };

  if (blocked) return i18n.t(`thankYouTexts.subTitles.${THANK_YOU_TEXT_DESKTOP.blocked}`);
  if (!category || !paymentStatus) return '';

  if (
    isPendingWithInstructions({
      isThereInstructions,
      status: paymentStatus,
      category
    })
  ) {
    return <>{expirationText}</>;
  }
  if (isDelayedPayment({ nextAction, status: paymentStatus })) {
    return i18n.t(`thankYouTexts.subTitles.${THANK_YOU_TEXT_DESKTOP[ACT_CODE].delayed}`);
  }
  if (is3DSPending({ nextAction, status: paymentStatus })) {
    return i18n.t(
      `thankYouTexts.subTitles.${THANK_YOU_TEXT_DESKTOP[ACT_CODE].three_domain_secured_failed}`
    );
  }

  const text = THANK_YOU_TEXT_DESKTOP[paymentStatus][category];
  if (text === EXPIRATION_TEXT) return getPendingText(Boolean(pdfLink));
  return text ? i18n.t(`thankYouTexts.subTitles.${text}`) : '';
};

const getExpirationText = (expiration, locale) => {
  if (!expiration) return '';
  const { time, day, month, year } = getDateFormatted(expiration, locale);
  return i18n.t('thankYouTexts.subTitles.text_pending_payment_expiration', {
    time,
    date: `${day} ${month} ${year}`
  });
};

export const getFlatNetworksFromCardsPMTsMapping = ({ cardPMTS = [] }) => {
  const mappings = compact((cardPMTS || []).map(type => type.mapping).flat());
  const brands = Array.from(
    new Set(mappings.map(mapping => mapping.brand?.toLowerCase()))
  );
  return {
    brands: brands || [],
    cardPMTS
  };
};
